import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import config from '../config';
import { Settings, Key } from 'lucide-react';
import { getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';

import { calculateVariation, renderVariation as renderVariationBase } from '../utils/variationUtils';
import { bornesDeLaPeriodeChoisie, calculatePreviousPeriod } from '../utils/periodUtils';
/**
 * Composant pour afficher les mots-clés Google Ads
 * Reprend exactement le même design que le composant Campagnes
 */
const KeywordsTable = ({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  formatNumber,
  isVisible = true,
  onToggleVisibility,
  selectedCampaignFilter,
  selectedQualityScoreFilter,
  selectedMatchTypeFilter,
  selectedCpcTierFilter,
  token,
  onCampaignsExtracted,
  userRole = 'USER',
  compareEnabled = false,
  comparisonType = ''
}) => {
  const [keywordsData, setKeywordsData] = useState(null);
  const [previousKeywordsData, setPreviousKeywordsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const itemsPerPage = 20;

  // Configuration des métriques pour ce composant
  const componentMetrics = useComponentMetrics('keywords', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);


  const renderVariation = (variation, isInversed = false) => (compareEnabled ? renderVariationBase(variation, isInversed) : null);

  // Map des mots-clés de la période précédente
  const previousKeywordsMap = useMemo(() => {
    if (!compareEnabled || !previousKeywordsData?.keywords) return {};
    const map = {};
    previousKeywordsData.keywords.forEach(keyword => {
      const key = keyword.keyword;
      if (key) map[key] = keyword;
    });
    return map;
  }, [compareEnabled, previousKeywordsData]);


  // Fonction pour charger les données des mots-clés
  const loadKeywords = async () => {
    if (!selectedAccount || !token) return;
    
    try {
      setLoading(true);
      setError(null);
      const requestBody = {
        sources: [selectedAccount.id],
        period: selectedPeriod
      };

      if (selectedPeriod === 'CUSTOM') {
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      const response = await axios.post(
        `${config.API_URL}/api/dashboard/keywords`,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        const keywordsData = response.data.data[0];
        setKeywordsData(keywordsData);
        
        // Extraire et envoyer les campagnes vers l'App.js
        if (onCampaignsExtracted && keywordsData.keywords) {
          onCampaignsExtracted(keywordsData);
        }
      } else {
        setKeywordsData(null);
      }
    } catch (err) {
      console.error('❌ [KEYWORDS-TABLE] Erreur:', err);
      setError('Erreur lors du chargement des mots-clés');
      setKeywordsData(null);
    } finally {
      setLoading(false);
    }
  };

  // Charger les données quand les dépendances changent
  useEffect(() => {
    if (selectedAccount && selectedPeriod && isVisible) {
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        return;
      }
      loadKeywords();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, isVisible]);

  // Charger les données de la période précédente pour la comparaison
  useEffect(() => {
    const loadPreviousKeywords = async () => {
      if (!selectedAccount || !token || !compareEnabled || !comparisonType) {
        setPreviousKeywordsData(null);
        return;
      }

      try {
        // Les bornes de la période regardée, puis celles de la comparaison. Le calcul qui
        // était ici s'arrêtait à **aujourd'hui** là où une période nommée s'arrête hier, et
        // ignorait LAST_60_DAYS, LAST_180_DAYS, LAST_365_DAYS, THIS_YEAR et LAST_YEAR.
        const plage = bornesDeLaPeriodeChoisie(selectedPeriod, customStartDate, customEndDate);
        if (!plage) return;

        const previousPeriod = calculatePreviousPeriod(plage.startDate, plage.endDate, comparisonType);

        const requestBody = {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: previousPeriod.startDate,
          customEndDate: previousPeriod.endDate
        };

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/keywords`,
          requestBody,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success && response.data.data && response.data.data.length > 0) {
          setPreviousKeywordsData(response.data.data[0]);
        } else {
          setPreviousKeywordsData(null);
        }
      } catch (err) {
        console.error('❌ [KEYWORDS-TABLE] Erreur chargement période précédente:', err);
        setPreviousKeywordsData(null);
      }
    };

    loadPreviousKeywords();
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, compareEnabled, comparisonType, token]);

  // Fonction de tri
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  // Trier les données
  const sortedKeywords = React.useMemo(() => {
    if (!keywordsData?.keywords) return [];
    
    return [...keywordsData.keywords].sort((a, b) => {
      const aValue = a[sortConfig.key] ?? 0;
      const bValue = b[sortConfig.key] ?? 0;
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [keywordsData, sortConfig]);

  // Filtrer par campagne et score de qualité si nécessaire
  const filteredKeywords = React.useMemo(() => {
    let filtered = sortedKeywords;
    
    // Filtre par campagne
    if (selectedCampaignFilter) {
      filtered = filtered.filter(keyword => 
        keyword.campaignId === selectedCampaignFilter.id ||
        keyword.campaignName === selectedCampaignFilter.name
      );
    }
    
    // Filtre par score de qualité
    if (selectedQualityScoreFilter !== null && selectedQualityScoreFilter !== undefined) {
      filtered = filtered.filter(keyword => {
        const keywordScore = keyword.qualityScore || 0;
        return keywordScore === selectedQualityScoreFilter;
      });
    }
    
    // Filtre par type de correspondance
    if (selectedMatchTypeFilter !== null && selectedMatchTypeFilter !== undefined) {
      filtered = filtered.filter(keyword => {
        return keyword.matchType === selectedMatchTypeFilter;
      });
    }
    
    // Filtre par palier CPC
    if (selectedCpcTierFilter !== null && selectedCpcTierFilter !== undefined) {
      filtered = filtered.filter(keyword => {
        const cpc = Number(keyword.cpc) || 0;
        return cpc >= selectedCpcTierFilter.min && cpc <= selectedCpcTierFilter.max;
      });
    }
    
    return filtered;
  }, [sortedKeywords, selectedCampaignFilter, selectedQualityScoreFilter, selectedMatchTypeFilter, selectedCpcTierFilter]);

  // Pagination
  const paginatedKeywords = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredKeywords.slice(startIndex, endIndex);
  }, [filteredKeywords, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredKeywords.length / itemsPerPage);

  // Vérifier si au moins un mot-clé a un score de qualité
  const hasQualityScore = React.useMemo(() => {
    return keywordsData?.keywords?.some(keyword => keyword.qualityScore !== null && keyword.qualityScore !== undefined) || false;
  }, [keywordsData]);

  // Calculer les totaux pour les mots-clés filtrés
  const filteredTotals = React.useMemo(() => {
    const totals = filteredKeywords.reduce((acc, keyword) => ({
      impressions: acc.impressions + keyword.impressions,
      clicks: acc.clicks + keyword.clicks,
      cost: acc.cost + keyword.cost,
      conversions: acc.conversions + keyword.conversions,
      conversionsValue: acc.conversionsValue + keyword.conversionsValue,
      keywordCount: acc.keywordCount + 1
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, keywordCount: 0 });

    totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
    totals.cpc = totals.clicks > 0 ? (totals.cost / totals.clicks) : 0;
    totals.conversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
    totals.cpa = totals.conversions > 0 ? (totals.cost / totals.conversions) : 0;
    totals.cos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0;

    return totals;
  }, [filteredKeywords]);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Key size={16} />&nbsp;Mots-clés</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {selectedCampaignFilter && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              padding: '4px 8px',
              background: '#e3f2fd',
              borderRadius: '4px',
              fontSize: '0.9em'
            }}>
              <span>Campagne: {selectedCampaignFilter.name}</span>
              <button
                onClick={() => window.setSelectedCampaignFilter && window.setSelectedCampaignFilter(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '2px',
                  color: '#666'
                }}
                title="Supprimer le filtre campagne"
              >
                ✕
              </button>
            </div>
          )}
          {/* Bouton configuration des métriques - visible uniquement pour non-clients */}
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          <button
            onClick={onToggleVisibility}
            className="section-toggle-btn"
            title="Masquer/Afficher la section"
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>
      </div>

      {!isVisible ? null : (
        <>
          {loading ? (
            <div className="loading-state" style={{ flexDirection: 'column' }}>
              <div className="loading-spinner"></div>
              Chargement des mots-clés...
            </div>
          ) : error ? (
            <div className="error-message">
              {error}
            </div>
          ) : !keywordsData || !keywordsData.keywords || keywordsData.keywords.length === 0 ? (
            <div className="empty-message">
              Aucun mot-clé trouvé pour la période sélectionnée
            </div>
          ) : (
            <>
              <div className="campaigns-table-wrapper">
            <table
              key={componentMetrics.metrics.join(',')}
              className="campaigns-table"
              style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}
            >
              <thead>
                <tr>
                  <th onClick={() => handleSort('keyword')} className="sortable">
                    Mot-clé {sortConfig.key === 'keyword' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {hasQualityScore && (
                    <th onClick={() => handleSort('qualityScore')} className="sortable numeric">
                      Score qualité {sortConfig.key === 'qualityScore' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('impressions') && (
                    <th onClick={() => handleSort('impressions')} className="sortable numeric">
                      Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('ctr') && (
                    <th onClick={() => handleSort('ctr')} className="sortable numeric">
                      CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('clics') && (
                    <th onClick={() => handleSort('clicks')} className="sortable numeric">
                      Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('cpc') && (
                    <th onClick={() => handleSort('cpc')} className="sortable numeric">
                      CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('cout') && (
                    <th onClick={() => handleSort('cost')} className="sortable numeric">
                      Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('taux_conv') && (
                    <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                      Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('conv') && (
                    <th onClick={() => handleSort('conversions')} className="sortable numeric">
                      Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('cpa') && (
                    <th onClick={() => handleSort('cpa')} className="sortable numeric">
                      CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('valeur_conv') && (
                    <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                      Valeur conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('cos') && (
                    <th onClick={() => handleSort('cos')} className="sortable numeric">
                      COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedKeywords.map((keyword, index) => {
                  const cosClass = getCosClassName(keyword.cos || 0, selectedAccount?.id || 'global', 'keywords');
                  
                  return (
                    <tr key={`${keyword.id}-${index}`}>
                      <td 
                        className="keyword-name keyword-tooltip"
                        onMouseEnter={(e) => {
                          const tooltip = e.target.querySelector('.tooltip-content');
                          if (tooltip) {
                            const rect = e.target.getBoundingClientRect();
                            tooltip.style.left = `${rect.left + rect.width/2}px`;
                            tooltip.style.top = `${rect.top - 10}px`;
                            tooltip.style.transform = 'translateX(-50%) translateY(-100%)';
                          }
                        }}
                      >
                        {keyword.keyword}
                        <div className="tooltip-content">
                          <div><strong>Campagne:</strong> {keyword.campaignName}</div>
                          <div><strong>Groupe d'annonces:</strong> {keyword.adGroupName}</div>
                        </div>
                      </td>
                      {hasQualityScore && (
                        <td className="numeric">
                          <span 
                            className={`quality-score quality-score-${keyword.qualityScore || 0} quality-score-tooltip`}
                            onMouseEnter={(e) => {
                              const tooltip = e.currentTarget.querySelector('.quality-tooltip-content');
                              if (tooltip) {
                                const rect = e.currentTarget.getBoundingClientRect();
                                tooltip.style.left = `${rect.left + rect.width/2}px`;
                                tooltip.style.top = `${rect.top - 10}px`;
                                tooltip.style.transform = 'translateX(-50%) translateY(-100%)';
                                tooltip.style.visibility = 'visible';
                                tooltip.style.opacity = '1';
                              }
                            }}
                            onMouseLeave={(e) => {
                              const tooltip = e.currentTarget.querySelector('.quality-tooltip-content');
                              if (tooltip) {
                                tooltip.style.visibility = 'hidden';
                                tooltip.style.opacity = '0';
                              }
                            }}
                          >
                            {keyword.qualityScore && keyword.qualityScore >= 1 && keyword.qualityScore <= 10 ? keyword.qualityScore : '-'}
                            {(keyword.creativeQualityScore || keyword.postClickQualityScore || keyword.searchPredictedCtr) && (
                              <div className="quality-tooltip-content">
                                <div><strong>Détails du score de qualité:</strong></div>
                                {keyword.creativeQualityScore && (
                                  <div><strong>Qualité de l'annonce:</strong> {keyword.creativeQualityScore}</div>
                                )}
                                {keyword.postClickQualityScore && (
                                  <div><strong>Expérience page de destination:</strong> {keyword.postClickQualityScore}</div>
                                )}
                                {keyword.searchPredictedCtr && (
                                  <div><strong>Taux de clics attendu:</strong> {keyword.searchPredictedCtr}</div>
                                )}
                              </div>
                            )}
                          </span>
                        </td>
                      )}
                      {(() => {
                        const prevKeyword = previousKeywordsMap[keyword.keyword] || {};
                        const hasPrev = compareEnabled && Object.keys(prevKeyword).length > 0;

                        return (
                          <>
                            {isComponentMetricSelected('impressions') && (
                              <td className="numeric">
                                {formatNumber(keyword.impressions)}
                                {hasPrev && renderVariation(calculateVariation(keyword.impressions, prevKeyword.impressions))}
                              </td>
                            )}
                            {isComponentMetricSelected('ctr') && (
                              <td className="numeric">
                                {formatNumber(keyword.ctr, 2)}%
                                {hasPrev && renderVariation(calculateVariation(keyword.ctr, prevKeyword.ctr))}
                              </td>
                            )}
                            {isComponentMetricSelected('clics') && (
                              <td className="numeric">
                                {formatNumber(keyword.clicks)}
                                {hasPrev && renderVariation(calculateVariation(keyword.clicks, prevKeyword.clicks))}
                              </td>
                            )}
                            {isComponentMetricSelected('cpc') && (
                              <td className="numeric">
                                €{formatNumber(keyword.cpc, 2)}
                                {hasPrev && renderVariation(calculateVariation(keyword.cpc, prevKeyword.cpc), true)}
                              </td>
                            )}
                            {isComponentMetricSelected('cout') && (
                              <td className="numeric">
                                €{formatNumber(keyword.cost, 2)}
                                {hasPrev && renderVariation(calculateVariation(keyword.cost, prevKeyword.cost), true)}
                              </td>
                            )}
                            {isComponentMetricSelected('taux_conv') && (
                              <td className="numeric">
                                {formatNumber(keyword.conversionRate, 2)}%
                                {hasPrev && renderVariation(calculateVariation(keyword.conversionRate, prevKeyword.conversionRate))}
                              </td>
                            )}
                            {isComponentMetricSelected('conv') && (
                              <td className="numeric">
                                {formatNumber(keyword.conversions, 1)}
                                {hasPrev && renderVariation(calculateVariation(keyword.conversions, prevKeyword.conversions))}
                              </td>
                            )}
                            {isComponentMetricSelected('cpa') && (
                              <td className={`numeric ${getCpaClassName(keyword.cpa, selectedAccount?.id || 'global', 'keywords')}`}>
                                {keyword.cpa >= 0 ? `€${formatNumber(keyword.cpa, 2)}` : '-'}
                                {hasPrev && renderVariation(calculateVariation(keyword.cpa, prevKeyword.cpa), true)}
                              </td>
                            )}
                            {isComponentMetricSelected('valeur_conv') && (
                              <td className="numeric">
                                €{formatNumber(keyword.conversionsValue, 2)}
                                {hasPrev && renderVariation(calculateVariation(keyword.conversionsValue, prevKeyword.conversionsValue))}
                              </td>
                            )}
                            {isComponentMetricSelected('cos') && (
                              <td className={`numeric ${cosClass}`}>
                                {formatNumber(keyword.cos, 1)}%
                                {hasPrev && renderVariation(calculateVariation(keyword.cos, prevKeyword.cos), true)}
                              </td>
                            )}
                          </>
                        );
                      })()}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {(() => {
                  // Calculer les totaux de la période précédente
                  const prevKeywords = previousKeywordsData?.keywords || [];
                  const prevTotals = prevKeywords.reduce((acc, keyword) => ({
                    impressions: acc.impressions + (keyword.impressions || 0),
                    clicks: acc.clicks + (keyword.clicks || 0),
                    cost: acc.cost + (keyword.cost || 0),
                    conversions: acc.conversions + (keyword.conversions || 0),
                    conversionsValue: acc.conversionsValue + (keyword.conversionsValue || 0)
                  }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

                  prevTotals.ctr = prevTotals.impressions > 0 ? (prevTotals.clicks / prevTotals.impressions * 100) : 0;
                  prevTotals.cpc = prevTotals.clicks > 0 ? (prevTotals.cost / prevTotals.clicks) : 0;
                  prevTotals.conversionRate = prevTotals.clicks > 0 ? (prevTotals.conversions / prevTotals.clicks * 100) : 0;
                  prevTotals.cpa = prevTotals.conversions > 0 ? (prevTotals.cost / prevTotals.conversions) : 0;
                  prevTotals.cos = prevTotals.conversionsValue > 0 ? (prevTotals.cost / prevTotals.conversionsValue * 100) : 0;

                  return (
                    <tr className="totals-row">
                      <td colSpan={hasQualityScore ? "2" : "1"}><strong>TOTAL ({filteredTotals.keywordCount} mots-clés)</strong></td>
                      {isComponentMetricSelected('impressions') && (
                        <td className="numeric">
                          <strong>{formatNumber(filteredTotals.impressions)}</strong>
                          {renderVariation(calculateVariation(filteredTotals.impressions, prevTotals.impressions))}
                        </td>
                      )}
                      {isComponentMetricSelected('ctr') && (
                        <td className="numeric">
                          <strong>{formatNumber(filteredTotals.ctr, 2)}%</strong>
                          {renderVariation(calculateVariation(filteredTotals.ctr, prevTotals.ctr))}
                        </td>
                      )}
                      {isComponentMetricSelected('clics') && (
                        <td className="numeric">
                          <strong>{formatNumber(filteredTotals.clicks)}</strong>
                          {renderVariation(calculateVariation(filteredTotals.clicks, prevTotals.clicks))}
                        </td>
                      )}
                      {isComponentMetricSelected('cpc') && (
                        <td className="numeric">
                          <strong>€{formatNumber(filteredTotals.cpc, 2)}</strong>
                          {renderVariation(calculateVariation(filteredTotals.cpc, prevTotals.cpc), true)}
                        </td>
                      )}
                      {isComponentMetricSelected('cout') && (
                        <td className="numeric">
                          <strong>€{formatNumber(filteredTotals.cost, 2)}</strong>
                          {renderVariation(calculateVariation(filteredTotals.cost, prevTotals.cost), true)}
                        </td>
                      )}
                      {isComponentMetricSelected('taux_conv') && (
                        <td className="numeric">
                          <strong>{formatNumber(filteredTotals.conversionRate, 2)}%</strong>
                          {renderVariation(calculateVariation(filteredTotals.conversionRate, prevTotals.conversionRate))}
                        </td>
                      )}
                      {isComponentMetricSelected('conv') && (
                        <td className="numeric">
                          <strong>{formatNumber(filteredTotals.conversions, 1)}</strong>
                          {renderVariation(calculateVariation(filteredTotals.conversions, prevTotals.conversions))}
                        </td>
                      )}
                      {isComponentMetricSelected('cpa') && (
                        <td className={`numeric ${getCpaClassName(filteredTotals.cpa, selectedAccount?.id || 'global', 'keywords')}`}>
                          <strong>{filteredTotals.cpa >= 0 ? `€${formatNumber(filteredTotals.cpa, 2)}` : '-'}</strong>
                          {renderVariation(calculateVariation(filteredTotals.cpa, prevTotals.cpa), true)}
                        </td>
                      )}
                      {isComponentMetricSelected('valeur_conv') && (
                        <td className="numeric">
                          <strong>€{formatNumber(filteredTotals.conversionsValue, 2)}</strong>
                          {renderVariation(calculateVariation(filteredTotals.conversionsValue, prevTotals.conversionsValue))}
                        </td>
                      )}
                      {isComponentMetricSelected('cos') && (
                        <td className={`numeric ${getCosClassName(filteredTotals.cos, selectedAccount?.id || 'global', 'keywords')}`}>
                          <strong>{formatNumber(filteredTotals.cos, 1)}%</strong>
                          {renderVariation(calculateVariation(filteredTotals.cos, prevTotals.cos), true)}
                        </td>
                      )}
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                <span>
                  Affichage {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredKeywords.length)} sur {filteredKeywords.length} mots-clés
                </span>
              </div>
              <div className="pagination-buttons">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  ← Précédent
                </button>
                <span className="page-numbers">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Suivant →
                </button>
              </div>
            </div>
          )}
            </>
          )}
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="keywords"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Mots-clés"
      />
    </div>
  );
};

export default KeywordsTable;